/** @forge-header
 * @id          subCent
 * @role        Sub-cent price arithmetic — mirror/mid/round in the integer centi-cent domain
 * @pattern     pure-fn
 * @purity      pure
 * @units       cents
 * @money-path  yes
 * @upstream    (none — leaf module)
 * @downstream  market/marketTransform | stores/{marketStore,accountStore} | ~20 more across views/, components/, hooks/
 * @exports     toCentiCents:fn | fromCentiCents:fn | roundToCentiCents:fn | mirrorCents:fn | blockerCents:fn | midCents:fn | validatePriceCentsInput:fn | validateKalshiPriceCentsInput:fn | stepKalshiPriceCents:fn | kalshiPriceBounds:fn | tickCentsFor:fn | validateReplacePriceCents:fn | stepReplacePriceCents:fn | PriceInputResult:type
 * @test        subCent.test.ts
 *
 * @invariant CENTI_CENT_DOMAIN | All mirror (100 − p) and rounding arithmetic on
 * cent-denominated prices happens in integer centi-cents (0–10,000, 0.01¢ grain).
 * The exact-mirror invariant only holds for integers: `100 - 58.3` is
 * `41.699999999999996` in float64 and breaks Map lookups / equality → phantom
 * levels. `mirrorCents(58.3)` is exactly `41.7`.
 */

import type { KalshiPriceRange } from '../../types';

/** Cents (0–100, ≤2dp) → integer centi-cents (0–10,000). */
export function toCentiCents(cents: number): number {
  return Math.round(cents * 100);
}

/** Integer centi-cents (0–10,000) → cents (0–100, ≤2dp). */
export function fromCentiCents(centiCents: number): number {
  return centiCents / 100;
}

/** Quantize a cent value to the 0.01¢ grain (canonical float per value). */
export function roundToCentiCents(cents: number): number {
  return Math.round(cents * 100) / 100;
}

/** Exact mirror `100 − p`, computed as `10000 − cc` in integer centi-cents. */
export function mirrorCents(cents: number): number {
  return (10000 - toCentiCents(cents)) / 100;
}

/**
 * Oscar blocker price `99 − p`: the opposite-side bid that blocks the level ONE
 * ahead of a take at `p` (blocking 99−p YES ≡ blocking p+1 on the take side).
 * An involution — `blockerCents(blockerCents(p)) === p` — which is what lets a
 * resting blocker order derive its take level back. 99, not 100: a 100−p
 * blocker would cross the trader's own take order under Kalshi taker_at_cross
 * STP. Twin: relay/src/kalshi/oscar.ts — keep the shared vector tests in sync.
 */
export function blockerCents(cents: number): number {
  return (9900 - toCentiCents(cents)) / 100;
}

/**
 * Midpoint of two cent prices.
 *
 * Integer inputs keep the legacy whole-cent round (`Math.round((a+b)/2)`) so
 * this is a provable no-op on whole-cent markets (mixed-version safety AND
 * product intent — a 1¢-tick market never shows a half-cent mid). Sub-cent
 * inputs quantize to the 0.01¢ grain so a 0.1/0.2 book mids to 0.15, not 0.
 */
export function midCents(a: number, b: number): number {
  if (Number.isInteger(a) && Number.isInteger(b)) return Math.round((a + b) / 2);
  return Math.round(((a + b) / 2) * 100) / 100;
}

// ── Order-form price validation (the ONE validator — DNL-006) ────────────

export type PriceInputResult =
  | { ok: true; priceCents: number }
  | { ok: false; error: string };

/**
 * Parse + validate a user-entered limit price in cents. THE shared validator
 * for every price-entry surface (order form, order-edit modal, position-close
 * modal — one helper, not three copies, per DNL-006).
 *
 * Accepts decimals to the 0.01¢ grain ("58.25", "0.5"); range is (0, 100)
 * EXCLUSIVE; tick alignment is checked in integer centi-cents (float `%`
 * drifts: `0.5 % 0.1 !== 0` in IEEE754). `tickCents` ≤ 0 skips the alignment
 * check. Whole-cent markets (tick 1) are never blocked for whole-cent input.
 * Returns the grain-canonical value — feed THIS to the order path, never the
 * raw string.
 */
export function validatePriceCentsInput(raw: string, tickCents: number): PriceInputResult {
  const text = raw.trim();
  if (!/^\d+(?:\.\d+)?$/.test(text)) {
    return { ok: false, error: 'Price must be a number in cents (e.g. 58.25)' };
  }
  const parsed = Number(text);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed >= 100) {
    return { ok: false, error: 'Price must be above 0 and below 100¢' };
  }
  const priceCc = Math.round(parsed * 100);
  if (Math.abs(parsed * 100 - priceCc) > 1e-6) {
    return { ok: false, error: 'Price precision is limited to 0.01¢' };
  }
  const tickCc = Math.round(tickCents * 100);
  if (tickCc > 0 && priceCc % tickCc !== 0) {
    return {
      ok: false,
      error: `Price must be a multiple of ${tickCents}¢ on this market`,
    };
  }
  return { ok: true, priceCents: priceCc / 100 };
}

interface ParsedKalshiPriceRange {
  startCc: number;
  endCc: number;
  stepCc: number;
}

/** Parse a dollar-probability string into integer centi-cents exactly. */
function kalshiDollarsToCentiCents(raw: string): number | null {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(raw.trim());
  if (!match) return null;
  const whole = Number(match[1]);
  const fraction = match[2] ?? '';
  if (!Number.isSafeInteger(whole)) return null;
  if (fraction.length > 4 && /[1-9]/.test(fraction.slice(4))) return null;
  const fraction4 = (fraction.slice(0, 4) + '0000').slice(0, 4);
  const value = whole * 10000 + Number(fraction4);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * Validate the complete Kalshi range contract before using any part of it.
 * Endpoint overlap between adjacent bands is allowed; interior overlap is not.
 * A malformed/partial contract returns null so callers use their explicit
 * mixed-version fallback rather than half-trusting it.
 */
function parseKalshiPriceRanges(
  ranges: KalshiPriceRange[] | undefined,
): ParsedKalshiPriceRange[] | null {
  if (!ranges?.length) return null;
  const parsed: ParsedKalshiPriceRange[] = [];
  for (const range of ranges) {
    const startCc = kalshiDollarsToCentiCents(range.start);
    const endCc = kalshiDollarsToCentiCents(range.end);
    const stepCc = kalshiDollarsToCentiCents(range.step);
    if (
      startCc === null ||
      endCc === null ||
      stepCc === null ||
      startCc < 0 ||
      endCc > 10000 ||
      startCc >= endCc ||
      stepCc <= 0 ||
      (endCc - startCc) % stepCc !== 0
    ) {
      return null;
    }
    parsed.push({ startCc, endCc, stepCc });
  }
  parsed.sort((a, b) => a.startCc - b.startCc || a.endCc - b.endCc);
  for (let i = 1; i < parsed.length; i += 1) {
    if (parsed[i].startCc < parsed[i - 1].endCc) return null;
  }
  return parsed;
}

/**
 * Validate a Kalshi new-order price against the market's authoritative,
 * potentially banded grid. No rounding or snapping occurs. Missing or malformed
 * range metadata falls back to the legacy 0.1¢ new-order check; venue rejection
 * remains lossless on this path.
 */
export function validateKalshiPriceCentsInput(
  raw: string,
  ranges: KalshiPriceRange[] | undefined,
  fallbackTickCents = 0.1,
): PriceInputResult {
  const basic = validatePriceCentsInput(raw, 0);
  if (!basic.ok) return basic;
  const parsed = parseKalshiPriceRanges(ranges);
  if (!parsed) return validatePriceCentsInput(raw, fallbackTickCents);
  const priceCc = toCentiCents(basic.priceCents);
  if (
    parsed.some(
      (range) =>
        priceCc >= range.startCc &&
        priceCc <= range.endCc &&
        (priceCc - range.startCc) % range.stepCc === 0,
    )
  ) {
    return basic;
  }
  const ticks = [...new Set(parsed.map((range) => fromCentiCents(range.stepCc)))];
  return {
    ok: false,
    error: `Price is off this market’s Kalshi grid (allowed increments: ${ticks.join('¢, ')}¢ by range)`,
  };
}

function nextPriceCcInRanges(
  currentCc: number,
  direction: -1 | 1,
  ranges: ParsedKalshiPriceRange[],
): number | null {
  let best: number | null = null;
  for (const range of ranges) {
    let candidate: number;
    if (direction > 0) {
      if (currentCc < range.startCc) {
        candidate = range.startCc;
      } else {
        const steps = Math.floor((currentCc - range.startCc) / range.stepCc) + 1;
        candidate = range.startCc + Math.max(0, steps) * range.stepCc;
      }
      while (candidate <= 0) candidate += range.stepCc;
      if (candidate > range.endCc || candidate >= 10000) continue;
      if (best === null || candidate < best) best = candidate;
    } else {
      if (currentCc > range.endCc) {
        candidate = range.endCc;
      } else if (currentCc <= range.startCc) {
        continue;
      } else {
        const steps = Math.ceil((currentCc - range.startCc) / range.stepCc) - 1;
        candidate = range.startCc + steps * range.stepCc;
      }
      while (candidate >= 10000) candidate -= range.stepCc;
      if (candidate < range.startCc || candidate <= 0) continue;
      if (best === null || candidate > best) best = candidate;
    }
  }
  return best;
}

/**
 * Move one valid Kalshi price level in either direction. This handles band
 * transitions such as 9.9¢ → 10¢ → 11¢ without manufacturing the invalid
 * 10.1¢ level. An off-grid current draft moves to the nearest valid level in
 * the requested direction only after an explicit arrow/button action.
 */
export function stepKalshiPriceCents(
  currentCents: number,
  direction: -1 | 1,
  ranges: KalshiPriceRange[] | undefined,
  fallbackTickCents = 0.1,
): number | null {
  const parsed = parseKalshiPriceRanges(ranges);
  const currentCc = Number.isFinite(currentCents) ? toCentiCents(currentCents) : direction > 0 ? 0 : 10000;
  if (parsed) {
    const next = nextPriceCcInRanges(currentCc, direction, parsed);
    return next === null ? null : fromCentiCents(next);
  }
  const stepCc = Math.max(1, toCentiCents(fallbackTickCents));
  const next =
    direction > 0
      ? (Math.floor(currentCc / stepCc) + 1) * stepCc
      : (Math.ceil(currentCc / stepCc) - 1) * stepCc;
  return next > 0 && next < 10000 ? fromCentiCents(next) : null;
}

/** Inclusive UI bounds inside Kalshi's exclusive 0¢/100¢ order domain. */
export function kalshiPriceBounds(
  ranges: KalshiPriceRange[] | undefined,
  fallbackTickCents = 0.1,
): { min: number; max: number } {
  return {
    min: stepKalshiPriceCents(0, 1, ranges, fallbackTickCents) ?? fallbackTickCents,
    max:
      stepKalshiPriceCents(100, -1, ranges, fallbackTickCents) ??
      mirrorCents(fallbackTickCents),
  };
}

/**
 * Minimum price increment in CENTS for a market — NEW-ORDER placement only.
 * Kalshi: assume deci_cent (0.1¢) — the loosest published
 * `price_level_structure`; a misaligned NEW order on a linear_cent market is
 * rejected venue-side with nothing lost. Polymarket: from the CLOB tickSize
 * (dollar-decimal string, e.g. "0.001" = 0.1¢); fallback 0.01 dollars = 1¢ —
 * correct for most sports moneylines, and whole-cent markets must never be
 * blocked by a stricter guess.
 *
 * @ai-danger NOT for cancel-first replacement — a venue rejection there
 * happens AFTER the resting order is gone. Use validateReplacePriceCents.
 */
export function tickCentsFor(
  venue: 'kalshi' | 'polymarket',
  polyTickSize: string | undefined,
): number {
  if (venue === 'kalshi') return 0.1;
  const t = Number(polyTickSize ?? '0.01');
  return Number.isFinite(t) && t > 0 ? t * 100 : 1;
}

/**
 * Validate a CANCEL-FIRST replacement price (DNL round-3 finding 1). Unlike a
 * new order — where a venue tick rejection costs nothing — a replacement
 * cancels the resting order BEFORE the venue sees the new price, so an
 * invalid tick loses the order and its queue position. Rules:
 *
 * - An UNCHANGED price is always allowed: the current resting price is
 *   tick-valid on its own market by existence.
 * - A known authoritative tick validates normally.
 * - An UNKNOWN tick is handled by the STRICTER policy, never a permissive
 *   guess: 'whole-cent' (Kalshi — integer cents are valid multiples of both
 *   published structures, linear_cent 1¢ and deci_cent 0.1¢) or 'reject'
 *   (Polymarket — ticks span 10¢ to 0.01¢, so no changed price is provably
 *   valid; replacement is disabled until the market resolves in the store).
 */
export function validateReplacePriceCents(args: {
  newPriceCents: number;
  /** The order's current resting price in cents, if known. */
  currentPriceCents: number | null;
  /** Authoritative tick in cents, or null when unknown. */
  tickCents: number | null;
  unknownTickPolicy: 'whole-cent' | 'reject';
}): PriceInputResult {
  const { newPriceCents, currentPriceCents, tickCents, unknownTickPolicy } = args;
  if (!Number.isFinite(newPriceCents) || newPriceCents <= 0 || newPriceCents >= 100) {
    return { ok: false, error: 'Price must be above 0 and below 100¢' };
  }
  const priceCc = Math.round(newPriceCents * 100);
  if (Math.abs(newPriceCents * 100 - priceCc) > 1e-6) {
    return { ok: false, error: 'Price precision is limited to 0.01¢' };
  }
  const canonical = priceCc / 100;
  if (currentPriceCents != null && Math.round(currentPriceCents * 100) === priceCc) {
    return { ok: true, priceCents: canonical };
  }
  const tickCcExact = tickCents == null ? null : tickCents * 100;
  const tickCc = tickCcExact == null ? null : Math.round(tickCcExact);
  const hasUsableTick =
    tickCcExact != null &&
    Number.isFinite(tickCcExact) &&
    tickCc != null &&
    tickCc > 0 &&
    Math.abs(tickCcExact - tickCc) <= 1e-6;
  if (hasUsableTick && tickCc != null) {
    if (priceCc % tickCc !== 0) {
      return { ok: false, error: `Price must be a multiple of ${tickCents}¢ on this market` };
    }
    return { ok: true, priceCents: canonical };
  }
  if (unknownTickPolicy === 'whole-cent') {
    if (priceCc % 100 !== 0) {
      return {
        ok: false,
        error:
          'This market’s tick size is unknown — a changed price must be a whole cent ' +
          '(or keep the current price). Cancel and re-place from the order form for sub-cent.',
      };
    }
    return { ok: true, priceCents: canonical };
  }
  return {
    ok: false,
    error:
      'This market’s tick size could not be resolved — price replacement is disabled ' +
      'so the resting order is not cancelled into a venue rejection.',
  };
}

/**
 * Step a CANCEL-FIRST replacement draft to the previous/next price that
 * `validateReplacePriceCents` will actually accept, or `null` when no such
 * price exists in that direction (#254 — the edit-modal arrow controls).
 *
 * This is the UI half of the replacement contract and it is deliberately
 * NOT a second opinion: every candidate is run through
 * `validateReplacePriceCents` under the caller's own contract, so the arrows
 * can never produce a price the submit gate would reject. The pre-cancel gate
 * in the replace path still re-resolves fresh metadata and re-validates — this
 * helper never substitutes for it.
 *
 * Tick resolution is strict. A zero, negative, non-finite, or sub-grain tick
 * is UNRESOLVED, not "unrestricted": Polymarket (`'reject'`) then returns
 * `null` and the caller disables both arrows, while Kalshi (`'whole-cent'`)
 * falls back to the conservative 100-centi-cent grid that is a valid multiple
 * of both published Kalshi structures. Never pass `tickCentsFor` output here —
 * it guesses 1¢ on missing Polymarket metadata (see its `@ai-danger` note).
 *
 * `currentPriceCents` is the unchanged-resting-price exception carried over
 * from the validator: a resting price is tick-valid by existence, so when it
 * lies strictly in the requested direction it competes as a candidate and wins
 * if it is nearest. Callers that do not get that exception (Polymarket, where
 * a resting price can predate a tick-regime change) pass `null`.
 */
export function stepReplacePriceCents(args: {
  draftPriceCents: number;
  direction: -1 | 1;
  currentPriceCents: number | null;
  tickCents: number | null;
  unknownTickPolicy: 'whole-cent' | 'reject';
}): number | null {
  const { draftPriceCents, direction, currentPriceCents, tickCents, unknownTickPolicy } = args;
  if (!Number.isFinite(draftPriceCents)) return null;
  const draftCc = toCentiCents(draftPriceCents);

  // Resolve the grid, or bail. `tickCents` below the 0.01¢ grain rounds to 0
  // centi-cents and is unusable — that is unresolved, not unrestricted.
  const resolvedTickCc =
    tickCents != null && Number.isFinite(tickCents) && tickCents > 0 ? toCentiCents(tickCents) : 0;
  const stepCc = resolvedTickCc > 0 ? resolvedTickCc : unknownTickPolicy === 'whole-cent' ? 100 : 0;
  if (stepCc <= 0) return null;

  // Strict floor/ceiling — never round back across the draft.
  const gridCc =
    direction > 0
      ? (Math.floor(draftCc / stepCc) + 1) * stepCc
      : (Math.ceil(draftCc / stepCc) - 1) * stepCc;

  const candidates = [gridCc];
  if (currentPriceCents != null && Number.isFinite(currentPriceCents)) {
    const currentCc = toCentiCents(currentPriceCents);
    const ahead = direction > 0 ? currentCc > draftCc : currentCc < draftCc;
    if (ahead) candidates.push(currentCc);
  }

  // Exclusive (0, 10000) — mirrors stepKalshiPriceCents' inline bound check.
  const inDomain = candidates.filter((cc) => cc > 0 && cc < 10000);
  // Nearest first, then take the first the validator actually accepts.
  inDomain.sort((a, b) => Math.abs(a - draftCc) - Math.abs(b - draftCc));
  for (const cc of inDomain) {
    const cents = fromCentiCents(cc);
    const check = validateReplacePriceCents({
      newPriceCents: cents,
      currentPriceCents,
      tickCents,
      unknownTickPolicy,
    });
    if (check.ok) return check.priceCents;
  }
  return null;
}
